import { parse } from "yaml";

import type { InscriptionDraft, TraitValue } from "./types";

const INSCRIPTION_ID = /^[0-9a-f]{64}i\d+$/i;
export const MAX_INSCRIPTION_FILE_BYTES = 400 * 1024;
export const MAX_BATCH_FILE_BYTES = 400 * 1024;
export const MAX_BATCH_ITEMS = 500;

export function filterBatchFilesForCompression<T extends { size: number }>(
  files: T[],
): { accepted: T[]; oversizedCount: number } {
  const accepted = files.filter((file) => file.size <= MAX_INSCRIPTION_FILE_BYTES);
  return { accepted, oversizedCount: files.length - accepted.length };
}

export function batchPayloadLimitMessage(totalBytes: number, compressed: boolean): string {
  if (totalBytes <= MAX_BATCH_FILE_BYTES) return "";
  return compressed
    ? "Brotli-compressed batch files must total 400 KB or less."
    : "Batch files must total 400 KB or less. Enable Brotli or remove files.";
}

export function batchRareSatSelectionLimit(
  mode: "files" | "hive" | "delegates",
  selectedFileCount: number,
  preparedDraftCount: number,
): number {
  return mode === "delegates" ? preparedDraftCount : selectedFileCount;
}

export function fitFilesWithinBatchLimit<T extends { size: number }>(
  files: T[],
  existingBytes = 0,
): { accepted: T[]; oversizedCount: number; overTotalCount: number } {
  const accepted: T[] = [];
  let oversizedCount = 0;
  let overTotalCount = 0;
  let total = existingBytes;

  for (const file of files) {
    if (file.size > MAX_INSCRIPTION_FILE_BYTES) {
      oversizedCount += 1;
      continue;
    }
    if (total + file.size > MAX_BATCH_FILE_BYTES) {
      overTotalCount += 1;
      continue;
    }
    accepted.push(file);
    total += file.size;
  }

  return { accepted, oversizedCount, overTotalCount };
}

interface BatchYamlItem {
  file?: string;
  delegate?: string;
  recipient?: string;
  postage?: number;
  metadata?: unknown;
  metaprotocol?: string;
  title?: string;
  traits?: Record<string, TraitValue>;
}

function parseItems(raw: string): BatchYamlItem[] {
  const parsed = parse(raw) as unknown;
  const value = typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) && "items" in parsed
    ? parsed.items
    : parsed;
  if (!Array.isArray(value)) throw new Error("Batch YAML must be a list, or an object with an items list");
  if (value.length > MAX_BATCH_ITEMS) throw new Error(`A batch can contain at most ${MAX_BATCH_ITEMS} items`);
  return value.map((item, index) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) throw new Error(`Batch item ${index + 1} must be an object`);
    return item as BatchYamlItem;
  });
}

function attributes(item: BatchYamlItem): InscriptionDraft["attributes"] {
  if (!item.title && !item.traits) return undefined;
  for (const [key, value] of Object.entries(item.traits ?? {})) {
    if (!key.trim() || !(value === null || typeof value === "string" || typeof value === "boolean" || (typeof value === "number" && Number.isSafeInteger(value)))) {
      throw new Error(`Invalid trait ${key || "(unnamed)"}`);
    }
  }
  return { title: item.title, traits: item.traits };
}

export function parseDelegateBatchYaml(raw: string): InscriptionDraft[] {
  return parseItems(raw).map((item, index) => {
    const delegate = item.delegate?.trim() ?? "";
    if (!INSCRIPTION_ID.test(delegate)) throw new Error(`Batch item ${index + 1} needs a valid delegate inscription ID`);
    return {
      id: crypto.randomUUID(),
      name: `delegate-${index + 1}`,
      delegateId: delegate,
      recipient: item.recipient?.trim() || undefined,
      postage: item.postage,
      metadata: item.metadata,
      metaprotocol: item.metaprotocol?.trim() || undefined,
      attributes: attributes(item),
    };
  });
}

export function applyBatchYaml(drafts: InscriptionDraft[], raw: string): InscriptionDraft[] {
  const items = parseItems(raw);
  return drafts.map((draft, index) => {
    const item = items.find((candidate) => candidate.file === draft.name) ?? items[index];
    if (!item) return draft;
    return {
      ...draft,
      recipient: item.recipient?.trim() || draft.recipient,
      postage: item.postage ?? draft.postage,
      metadata: item.metadata ?? draft.metadata,
      metaprotocol: item.metaprotocol?.trim() || draft.metaprotocol,
      attributes: attributes(item) ?? draft.attributes,
    };
  });
}

export function buildBulkMetadataTemplate(fileNames: string[]): string {
  return fileNames.map((fileName, index) => {
    const name = fileName.replace(/\.[^.]+$/, "") || fileName;
    const metadata = JSON.stringify({ name, collection: "My collection", edition: index + 1 });
    return `${fileName} | ${metadata}`;
  }).join("\n");
}

export function applyBulkMetadataPaste(drafts: InscriptionDraft[], raw: string): InscriptionDraft[] {
  const lines = raw.split(/\r?\n/)
    .map((value, index) => ({ value: value.trim(), line: index + 1 }))
    .filter(({ value }) => Boolean(value) && !value.startsWith("#"));
  if (lines.length > MAX_BATCH_ITEMS) throw new Error(`Bulk metadata can contain at most ${MAX_BATCH_ITEMS} lines`);

  const knownFiles = new Set(drafts.map((draft) => draft.name));
  const metadataByFile = new Map<string, unknown>();
  for (const { value, line } of lines) {
    const tabAt = value.indexOf("\t");
    const pipeAt = value.indexOf("|");
    const separatorAt = tabAt > 0 && (pipeAt < 0 || tabAt < pipeAt) ? tabAt : pipeAt;
    if (separatorAt < 1) throw new Error(`Line ${line}: use FILENAME | {"name":"…"}`);

    const fileName = value.slice(0, separatorAt).trim();
    const metadataJson = value.slice(separatorAt + 1).trim();
    if (!knownFiles.has(fileName)) throw new Error(`Line ${line}: “${fileName}” is not in this batch`);
    if (metadataByFile.has(fileName)) throw new Error(`Line ${line}: “${fileName}” is listed twice`);

    let metadata: unknown;
    try {
      metadata = JSON.parse(metadataJson) as unknown;
    } catch {
      throw new Error(`Line ${line}: metadata must be valid JSON`);
    }
    if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
      throw new Error(`Line ${line}: metadata must be a JSON object`);
    }
    metadataByFile.set(fileName, metadata);
  }

  return drafts.map((draft) => metadataByFile.has(draft.name)
    ? { ...draft, metadata: metadataByFile.get(draft.name) }
    : draft);
}
